import './registration-modal.scss';

import { isEmpty, kebabCase, orderBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useRegistrationHandling } from '../../../../data/data/registrations/use-registration-handling';
import { useRegistrationItem } from '../../../../data/data/registrations/use-registration-item';
import { useRegistrationRetention } from '../../../../data/data/registrations/use-registration-retention';
import { getRegistrationAddress } from '../../../../data/data/registrations/utils';
import { getInputValidation } from '../../../../data/GraphQL/api/graphql-errors';
import {
  RegistrationPersonItemDataFragment,
  RegistrationPersonUpdateInput,
  RegistrationStatus,
} from '../../../../data/types/api/graphql';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { Tag, TagType } from '../../../../elements/tag/tag';
import { RegistrationPersonModal } from '../registration-person-modal/registration-person-modal';
import { RegistrationPersonPrint } from '../registration-person-print/registration-person-print';
import { RegistrationRejectPrompt } from '../registration-reject-prompt/registration-reject-prompt';
import { RegistrationPerson } from './registration-person';

const statusTagType: { [key in RegistrationStatus]: TagType } = {
  [RegistrationStatus.Open]: TagType.ORANGE,
  [RegistrationStatus.Accepted]: TagType.ACTIVE,
  [RegistrationStatus.Rejected]: TagType.NOTACTIVE,
};

type RegistrationModalProps = {
  registrationId: string | null | undefined;
  onPatient?: (value: RegistrationPersonItemDataFragment) => void;
  onClose?: () => void;
};

/**
 * One submission is one household: the address and the practice come from the
 * parent form and are shown once, the person block repeats.
 */
export function RegistrationModal({
  registrationId,
  onPatient,
  onClose,
}: RegistrationModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const {
    data: registrationItemData,
    loading: registrationItemLoading,
    refetch,
  } = useRegistrationItem(registrationId);

  const {
    accept,
    reject,
    updatePerson,
    loading: handlingLoading,
  } = useRegistrationHandling();

  const { data: retention } = useRegistrationRetention();

  const { getFullName } = useFormatters();

  const [acceptPromptOpen, setAcceptPromptOpen] = useState<boolean>(false);
  const [rejectPromptOpen, setRejectPromptOpen] = useState<boolean>(false);
  const [editPerson, setEditPerson] =
    useState<RegistrationPersonItemDataFragment>();
  const [printPerson, setPrintPerson] =
    useState<RegistrationPersonItemDataFragment>();

  const isOpen = useMemo(
    () => registrationItemData?.status === RegistrationStatus.Open,
    [registrationItemData?.status]
  );

  /**
   * Accepting and rejecting are final and the server refuses a second attempt
   * — two open tabs are enough to cause one. The server message is the useful
   * one ("already handled"), so show it, then reload the detail so the screen
   * tells the same story as the database.
   */
  const onHandleError = useCallback(
    async (error: unknown) => {
      let message = (error as Error)?.message;
      const validation = getInputValidation(error);

      if (validation?.validation === 'AlreadyExists') {
        // An email address that is already taken would never get its own
        // login, so the server refuses and names the person; say who it is,
        // so the practice knows whose address to change.
        const person = registrationItemData?.persons.find(
          option => option.id === validation.target
        );
        message = person
          ? intl.formatMessage(
              { id: 'registrations.accept.email-in-use-person' },
              { name: getFullName(person) }
            )
          : intl.formatMessage({ id: 'registrations.accept.email-in-use' });
      } else if (isEmpty(message)) {
        message = intl.formatMessage({ id: 'common.error' });
      }

      showAlertMessage({ type: AlertMessageType.ERROR, message });

      await refetch();
    },
    [getFullName, intl, refetch, registrationItemData, showAlertMessage]
  );

  const onHandleUpdatePerson = useCallback(
    async (input: RegistrationPersonUpdateInput) => {
      // A refusal is thrown on to the modal, which shows it on the fields.
      await updatePerson(input);

      setEditPerson(undefined);
      showAlertMessage({
        type: AlertMessageType.SUCCESS,
        message: intl.formatMessage({ id: 'registrations.edit.success' }),
      });
    },
    [intl, showAlertMessage, updatePerson]
  );

  const onHandleAccept = useCallback(async () => {
    if (!registrationItemData) return;

    try {
      await accept(registrationItemData.id);

      showAlertMessage({
        type: AlertMessageType.SUCCESS,
        message: intl.formatMessage({ id: 'registrations.accept.success' }),
      });
    } catch (error) {
      await onHandleError(error);
    }
  }, [accept, intl, onHandleError, registrationItemData, showAlertMessage]);

  const onHandleReject = useCallback(
    async (reason: string) => {
      if (!registrationItemData) return;

      try {
        await reject(registrationItemData.id, reason);

        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({ id: 'registrations.reject.success' }),
        });

        setRejectPromptOpen(false);
      } catch (error) {
        setRejectPromptOpen(false);

        await onHandleError(error);
      }
    },
    [intl, onHandleError, registrationItemData, reject, showAlertMessage]
  );

  // The form numbers the person blocks; show them back in that order.
  const persons = useMemo(
    () =>
      orderBy(registrationItemData?.persons ?? [], option => option.sequence),
    [registrationItemData?.persons]
  );

  // Accepting only creates a patient for persons old enough for an account;
  // the prompt names the others, so the practice confirms knowing that.
  const noAccountPersons = useMemo(
    () => persons.filter(option => !option.createsAccount),
    [persons]
  );

  return (
    <>
      <Modal
        testId="registration"
        loading={registrationItemLoading}
        width={ModalWidth.LARGE}
        title={<CustomFormattedMessage id="registrations.detail.title" />}
        onClose={onClose}
        buttons={
          isOpen ? (
            <>
              <Button
                testId="registration-reject-action"
                type={ButtonType.SECONDARY}
                inverted
                disabled={handlingLoading}
                onClick={() => setRejectPromptOpen(true)}
              >
                <CustomFormattedMessage id="registrations.button.reject" />
              </Button>

              <Button
                testId="registration-accept-action"
                icon={handlingLoading ? ButtonIcon.LOADING : ButtonIcon.NONE}
                disabled={handlingLoading}
                onClick={() => setAcceptPromptOpen(true)}
              >
                <CustomFormattedMessage id="registrations.button.accept" />
              </Button>
            </>
          ) : undefined
        }
      >
        {registrationItemData && (
          <div
            data-test-id="registrations.detail"
            className="registration-modal-info"
          >
            <div className="registration-modal-info__tags">
              <Tag
                type={statusTagType[registrationItemData.status]}
                text={
                  <CustomFormattedMessage
                    id={`registrations.status.${kebabCase(
                      registrationItemData.status
                    )}`}
                  />
                }
              />
            </div>

            <div className="registration-modal-info__household">
              <h4>
                <CustomFormattedMessage id="registrations.address" />
              </h4>
              <p data-test-id="registrations.detail.address">
                {getRegistrationAddress(registrationItemData)}
              </p>

              <h4>
                <CustomFormattedMessage id="registrations.submitted-at" />
              </h4>
              <p>
                {intl.formatDate(registrationItemData.submittedAt, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>

              {registrationItemData.handledAt && (
                <>
                  <h4>
                    <CustomFormattedMessage id="registrations.handled-at" />
                  </h4>
                  <p>
                    {intl.formatDate(registrationItemData.handledAt, {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </>
              )}

              {!isEmpty(registrationItemData.rejectionReason) && (
                <>
                  <h4>
                    <CustomFormattedMessage id="registrations.reject.reason" />
                  </h4>
                  <p data-test-id="registrations.detail.rejection-reason">
                    {registrationItemData.rejectionReason}
                  </p>
                </>
              )}
            </div>

            <h3 className="registration-modal-info__heading">
              <CustomFormattedMessage
                id="registrations.persons-heading"
                values={{ count: registrationItemData.personCount }}
              />
            </h3>

            {persons.map(option => (
              <RegistrationPerson
                key={option.id}
                person={option}
                onPatient={onPatient}
                onEdit={isOpen ? setEditPerson : undefined}
                onPrint={setPrintPerson}
              />
            ))}
          </div>
        )}
      </Modal>

      {acceptPromptOpen && (
        <ModalPrompt
          testId="registration-accept"
          onConfirm={onHandleAccept}
          onClose={() => setAcceptPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage
              id="registrations.accept.content"
              values={{
                count: persons.length - noAccountPersons.length,
              }}
            />
          </p>

          {!isEmpty(noAccountPersons) && (
            <div data-test-id="registration-accept.no-account">
              <p>
                <CustomFormattedMessage id="registrations.accept.no-account" />
              </p>
              <ul>
                {noAccountPersons.map(option => (
                  <li key={option.id}>{getFullName(option)}</li>
                ))}
              </ul>
            </div>
          )}

          {retention && (
            <p>
              <CustomFormattedMessage
                id="registrations.accept.retention"
                values={{ acceptedDays: retention.acceptedDays }}
              />
            </p>
          )}
        </ModalPrompt>
      )}

      {editPerson && (
        <RegistrationPersonModal
          person={editPerson}
          onConfirm={onHandleUpdatePerson}
          onClose={() => setEditPerson(undefined)}
        />
      )}

      {registrationItemData && printPerson && (
        <RegistrationPersonPrint
          key={printPerson.id}
          registration={registrationItemData}
          person={printPerson}
          onClose={() => setPrintPerson(undefined)}
        />
      )}

      {rejectPromptOpen && (
        <RegistrationRejectPrompt
          loading={handlingLoading}
          rejectedDays={retention?.rejectedDays}
          onConfirm={onHandleReject}
          onClose={() => setRejectPromptOpen(false)}
        />
      )}
    </>
  );
}
